import React, { useState } from 'react';
import { Pencil, Trash2, UserPlus, Users2 } from 'lucide-react';
import { useDeleteUser, useUsers } from '../hooks/useApi';
import UserModal from '../components/UserModal';
import {
    Avatar,
    Badge,
    Button,
    Card,
    ConfirmDialog,
    DataTable,
    Dropdown,
    EmptyState,
    MenuItem,
    PageHeader,
    Pagination,
    TableToolbar,
} from '../components/ui';

const ROLES = [
    'Manager',
    'Head of Department',
    'Developer',
    'System Administrator',
    'Employee',
];

const Users = () => {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({ is_active: 'all' });
    const [selectedUser, setSelectedUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const { data: result, isLoading } = useUsers({ ...filters, search, page });
    const users = result?.data || [];
    const deleteUser = useDeleteUser();

    const totalPages = result?.totalPages || 1;
    const total = result?.total || users.length;
    const pageSize = total && totalPages ? Math.ceil(total / totalPages) : users.length || 10;

    const openCreate = () => {
        setSelectedUser(null);
        setIsModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;
        try {
            await deleteUser.mutateAsync(userToDelete.id);
            setUserToDelete(null);
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    };

    const columns = [
        {
            key: 'name',
            header: 'Name',
            render: (u) => (
                <span className="flex items-center gap-2.5">
                    <Avatar name={`${u.first_name} ${u.last_name}`} size="md" />
                    <span className="min-w-0">
                        <span className="block font-medium text-ink-900 truncate">
                            {u.first_name} {u.last_name}
                        </span>
                        <span className="block text-micro text-ink-500 truncate">{u.email}</span>
                    </span>
                </span>
            ),
        },
        {
            key: 'role',
            header: 'Role',
            render: (u) => <Badge variant="primary">{u.role}</Badge>,
        },
        {
            key: 'department',
            header: 'Department',
            render: (u) => <span className="text-ink-700">{u.department || '—'}</span>,
        },
        {
            key: 'is_active',
            header: 'Status',
            render: (u) => (
                <Badge variant={u.is_active ? 'success' : 'neutral'} dot>
                    {u.is_active ? 'Active' : 'Inactive'}
                </Badge>
            ),
        },
        {
            key: 'actions',
            header: '',
            align: 'right',
            width: 48,
            render: (u) => (
                <Dropdown label={`Actions for ${u.first_name} ${u.last_name}`}>
                    <MenuItem
                        icon={Pencil}
                        onClick={() => {
                            setSelectedUser(u);
                            setIsModalOpen(true);
                        }}
                    >
                        Edit user
                    </MenuItem>
                    <MenuItem icon={Trash2} danger onClick={() => setUserToDelete(u)}>
                        Delete user
                    </MenuItem>
                </Dropdown>
            ),
        },
    ];

    return (
        <>
            <PageHeader
                title="Users"
                description="Team members, their roles and access across the system."
                breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Users' }]}
                actions={
                    <Button variant="primary" icon={UserPlus} onClick={openCreate}>
                        Add user
                    </Button>
                }
            />

            <Card>
                <TableToolbar
                    search={search}
                    onSearchChange={(v) => {
                        setSearch(v);
                        setPage(1);
                    }}
                    searchPlaceholder="Search name or email…"
                    filters={
                        <>
                            <select
                                className="input w-auto"
                                aria-label="Filter by role"
                                value={filters.role || ''}
                                onChange={(e) => {
                                    setFilters((prev) => ({ ...prev, role: e.target.value || undefined }));
                                    setPage(1);
                                }}
                            >
                                <option value="">All roles</option>
                                {ROLES.map((r) => (
                                    <option key={r} value={r}>
                                        {r}
                                    </option>
                                ))}
                            </select>

                            <select
                                className="input w-auto"
                                aria-label="Filter by account status"
                                value={filters.is_active ?? 'all'}
                                onChange={(e) => {
                                    setFilters((prev) => ({ ...prev, is_active: e.target.value }));
                                    setPage(1);
                                }}
                            >
                                <option value="all">Active &amp; inactive</option>
                                <option value="true">Active only</option>
                                <option value="false">Inactive only</option>
                            </select>
                        </>
                    }
                />

                <DataTable
                    columns={columns}
                    rows={users}
                    loading={isLoading}
                    empty={
                        <EmptyState
                            icon={Users2}
                            title={search || filters.role ? 'No users match this search' : 'No users yet'}
                            description={
                                search || filters.role
                                    ? 'Clear the search or role filter to see everyone.'
                                    : 'Add the first team member to grant access to the system.'
                            }
                            action={
                                <Button variant="primary" icon={UserPlus} onClick={openCreate}>
                                    Add user
                                </Button>
                            }
                        />
                    }
                />

                {users.length > 0 && (
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        total={total}
                        pageSize={pageSize}
                        onPageChange={setPage}
                    />
                )}
            </Card>

            {isModalOpen && <UserModal user={selectedUser} onClose={() => setIsModalOpen(false)} />}

            <ConfirmDialog
                isOpen={Boolean(userToDelete)}
                title="Delete user"
                message={`This removes ${userToDelete?.first_name} ${userToDelete?.last_name} from the system. If they still have active records they will be deactivated instead.`}
                confirmText="Delete user"
                variant="danger"
                isLoading={deleteUser.isPending}
                onConfirm={confirmDelete}
                onClose={() => setUserToDelete(null)}
            />
        </>
    );
};

export default Users;
